/**
 * `WorkflowTemplate.ts` — the starter document a new workflow is seeded from
 * when a project has none yet.
 *
 * This is the human-authoring surface only: no identity (the file model stamps
 * uid / project) and no prepared scripts — the workflow-builder
 * writes each step's `scripts` list later.
 *
 * It is an EMPTY SKELETON, not demo content: fields are seeded
 * PRESENT-BUT-EMPTY (`""`) so the authored view renders their bare labels with
 * the editor's ghost hints, and the author types their answer where the caret
 * already is. No `<angle-bracket>` placeholders and no invented example
 * values — a placeholder has to be found and deleted before it can be
 * replaced, and one left behind ships as if it were real content.
 *
 * The only values written are ones that are genuinely the right default
 * rather than a stand-in: the Back / Next display settings, the required
 * completion-step marker, and a first step named "Overview". Structurally
 * valid on its own (a name plus one ending step, no unresolved handoffs) — so
 * a freshly seeded workflow is PLAYABLE from the first moment, carrying
 * warnings but no errors.
 *
 * `triggers` is deliberately NOT seeded even though the model supports it: a
 * step needs no branch buttons by default, and the reviewer raises them once
 * there is something to branch to. Seeding every label a step *may* carry
 * turns the skeleton back into a form to clear out.
 */

import {
  WorkflowDocument,
  WorkflowStep,
} from "./WorkflowDocument";

/** The default first step. A real, usable name — not a placeholder to replace. */
const K_FIRST_STEP_NAME = "Overview";

/** Used only when the project has no name; the name cannot be blank (validation errors). */
const K_FALLBACK_NAME = "Untitled workflow";

/** Builds the default authored skeleton, named after the project when available. */
export function buildDefaultWorkflow(workflowName?: string): WorkflowDocument {
  const document = new WorkflowDocument();
  document.workflowName =
    workflowName === undefined || workflowName.trim().length === 0
      ? K_FALLBACK_NAME
      : workflowName.trim();
  document.description = "";
  document.expectedDuration = "";
  // A real default rather than a stand-in: a guide has to speak something, and
  // English is what every other default in the app assumes.
  document.language = "en";
  document.product = "";

  const step = new WorkflowStep();
  step.name = K_FIRST_STEP_NAME;
  step.scene = "";
  // Present-but-empty: the view renders bare labels for the author to fill in.
  // The two are different pieces of writing and both are seeded — `Context` is
  // the one-line summary the guide recognises the step by, `Instructions` is
  // the copy the learner reads, and an empty one of those simply shows nothing
  // in the play panel.
  step.stepContext = "";
  step.stepInstructions = "";
  // Back's label is OPTIONAL — the button is there whenever the learner has
  // somewhere to go back to — so present-but-empty seeds the unlabelled arrow
  // with its label line still there to be filled in. Next is the one where the
  // label IS the display switch, so it gets a real default: a first step needs
  // a way to finish, and "Next" is the wording the panel has always used.
  step.backButton = "";
  step.nextButton = "Next";
  step.isCompletionStep = true;

  document.steps.push(step);
  return document;
}
